import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button, Card, CardBody, CardHeader, FormSection, LoadingState, Pill, RecordDetails, ScrollBar, TileGrid } from 'admination-react'
import { loadPersonOverview, type PersonOverview as Overview } from '@/core/data/person'
import { currentAutosaveScope } from '@/core/api/autosaveScope'
import { useApp } from '@/core/state/appStore'
import { currentVariant } from '@/core/data/session'
import { formatDatum } from '@/core/lib/dates'
import { NO_UE_CAP, descriptionFor } from '@/variants'
import { aufgabeForName } from '@/core/data/aufgabe'
import { ModalHeader } from '../ModalHeader'

const LABELS: Record<string, string> = {
  vorname: 'Vorname', nachname: 'Nachname', geburtsdatum: 'Geburtsdatum', email: 'E-Mail', phone: 'Telefon', mobil: 'Mobiltelefon',
  strasse: 'Straße', hausnummer: 'Hausnummer', adresszusatz: 'Adresszusatz', plz: 'PLZ', ort: 'Ort', kundennummer: 'Kundennummer', geschlecht: 'Geschlecht',
  aufgabe: 'Aktueller Schritt', massnahme: 'Maßnahme', members: 'Coach', agentur: 'Kostenträger', org_zeichen: 'Organisationszeichen',
  beginn: 'Beginn', ende: 'Ende', teilnahmebeginn: 'Teilnahmebeginn', status_seit: 'Status seit', sprache: 'Sprache',
  formular_entwuerfe: 'Formularentwürfe', akte_abgeschlossen_at: 'Akte abgeschlossen am',
  zurueckgezogen_am: 'Zurückgezogen am', zurueckgezogen_grund: 'Rückzugsgrund', zurueckgezogen_grund_art: 'Art des Rückzugs',
  eingeschraenkt_at: 'Eingeschränkt am', einschraenkung_grund: 'Grund der Einschränkung',
  created_at: 'Erstellt am', updated_at: 'Aktualisiert am', datum: 'Datum', inhalt: 'Inhalt', anwesenheit: 'Anwesenheit', modul: 'Modul', ue: 'Unterrichtseinheiten',
  signatur: 'Unterschriften', rolle: 'Rolle', kanal: 'Kanal', ersetzt_am: 'Ersetzt am', name: 'Name', data: 'Dokumentinhalt', file_type: 'Dateityp',
  profil: 'Profil', lebenslauf: 'Werdegang', von: 'Von', bis: 'Bis', eintragsart: 'Art des Eintrags', taetigkeit: 'Tätigkeit', einrichtung: 'Einrichtung', hinweis: 'Hinweis',
  zeitl_umf_max_ue: 'Maximale Unterrichtseinheiten', bezeichnung: 'Bezeichnung', kosten: 'Kosten', avgs_nummer: 'AVGS-Nummer', gueltigkeit_beginn: 'Gültig ab', gueltigkeit_ende: 'Gültig bis',
}
const hidden = (key: string) => key === 'id' || key.endsWith('_id') || ['created_by', 'updated_by', 'variant', 'bucket_address', 'hash_vorher', 'hash_nachher', 'ip', 'signatur', 'erfasst_von', 'eingeschraenkt_von', 'zurueckgezogen_von', 'passwort', 'password', 'token'].includes(key)
const labelFor = (key: string) => LABELS[key] ?? key.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase())
const COACHING_CARD_FIELDS = ['avgs_nummer', 'gueltigkeit_beginn', 'gueltigkeit_ende', 'zeitl_umf_max_ue', 'kosten'] as const
function display(value: string | number | boolean, key?: string) {
  if (key === 'zeitl_umf_max_ue' && value === NO_UE_CAP) return 'Keine Begrenzung'
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDatum(value)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const date = new Date(value)
    if (!Number.isNaN(date.valueOf())) return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(date)
  }
  return String(value)
}
function Facts({ values }: { values: Record<string, unknown> }) {
  const entries = Object.entries(values).filter(([key, value]) => !hidden(key) && value != null && value !== ''
    && !(['avgs_nummer', 'zeitl_umf_max_ue'].includes(key) && value === 0)
    && !(['gueltigkeit_beginn', 'gueltigkeit_ende'].includes(key) && value === '1970-01-01'))
  const facts = entries.filter(([, value]) => typeof value !== 'object').map(([key, value]) => ({ label: labelFor(key), value: display(value as string | number | boolean, key) }))
  return <>
    {facts.length > 0 && <RecordDetails items={facts} />}
    {entries.filter(([, value]) => typeof value === 'object').map(([key, value]) => {
      if (Array.isArray(value)) return value.length ? <FormSection key={key} label={labelFor(key)}>{value.filter((item) => item != null).map((item, i) => typeof item === 'object' && item != null ? <Facts key={i} values={item} /> : <RecordDetails key={i} items={[{ label: labelFor(key), value: display(item, key) }]} />)}</FormSection> : null
      return value && Object.keys(value).length ? <FormSection key={key} label={labelFor(key)}><Facts values={value as Record<string, unknown>} /></FormSection> : null
    })}
  </>
}

function CoachingCards({ items, onOpen }: { items: Overview['coachings']; onOpen: (item: Overview['coachings'][number]) => void }) {
  return <TileGrid aria-label="Coachings">{items.map((item) => {
    const facts = COACHING_CARD_FIELDS.flatMap((key) => {
      const value = item.coaching[key]
      if (value == null || value === '' || (['avgs_nummer', 'zeitl_umf_max_ue'].includes(key) && value === 0)
        || (key.startsWith('gueltigkeit_') && value === '1970-01-01') || !['string', 'number', 'boolean'].includes(typeof value)) return []
      return [{ label: labelFor(key), value: display(value as string | number | boolean, key) }]
    })
    return <Card key={item.id} onClick={() => onOpen(item)}>
      <CardHeader>{String((item.coaching.massnahme as { bezeichnung?: string } | null)?.bezeichnung ?? 'Coaching')}</CardHeader>
      <CardBody>{item.participation.aufgabe?.name ?? 'Kein Schritt hinterlegt'}</CardBody>
      {(item.participation.beginn || item.participation.ende) && <CardBody>{[
        item.participation.beginn && display(item.participation.beginn),
        item.participation.ende && display(item.participation.ende),
      ].filter(Boolean).join(' bis ')}</CardBody>}
      {facts.length > 0 && <RecordDetails items={facts} />}
    </Card>
  })}</TileGrid>
}

export function PersonOverview({ coachingId, actions, coachingOnly = false }: { coachingId: string | null; actions?: ReactNode; coachingOnly?: boolean }) {
  const { state, dispatch } = useApp()
  const scope = currentAutosaveScope()
  const [load, setLoad] = useState<{ key: string; data?: Overview; error?: string }>({ key: '' })
  const [retry, setRetry] = useState(0)
  const key = JSON.stringify([scope, coachingId, state.dashboardVersion, retry])
  useEffect(() => {
    let cancelled = false
    if (!coachingId) return
    loadPersonOverview(coachingId).then((data) => {
      if (cancelled) return
      dispatch({ type: "SET_PERSON", coachingId, participantId: data.person.id })
      setLoad({ key, data })
    }).catch((error: unknown) => {
      if (!cancelled) setLoad({ key, error: error instanceof Error ? error.message : 'Die Person konnte nicht geladen werden.' })
    })
    return () => { cancelled = true }
  }, [coachingId, key, dispatch])
  const scrollerRef = useRef<HTMLElement>(null)
  const result = load.key === key ? load : undefined
  const data = result?.data
  const coachings = data?.coachings.filter((item) => !coachingOnly || item.id === coachingId) ?? []
  const VariantSections = descriptionFor(currentVariant()).personOverviewSections
  return <>
    <ModalHeader>{state.screen?.tab === 'uebersicht' && actions}</ModalHeader>
    <main className="form" data-scroll-host ref={scrollerRef}>
      {result?.error ? <FormSection label="Person konnte nicht geladen werden"><p role="alert">{result.error}</p><Pill><Button onClick={() => setRetry((n) => n + 1)}>Erneut versuchen</Button></Pill></FormSection>
        : !data ? <LoadingState>Person wird geladen …</LoadingState>
          : <>
            {data.unavailableCoachings > 0 && <p role="status">Einige verknüpfte Coachings sind eingeschränkt oder nicht mehr verfügbar. Ihre Daten werden hier nicht angezeigt.</p>}
            {state.screen?.tab === 'uebersicht' && <>
              <FormSection label={[data.person.vorname, data.person.nachname].join(' ')}>
                <RecordDetails summary items={[
                  { label: 'Coachings', value: data.coachings.length },
                  { label: 'Dokumente', value: data.coachings.reduce((sum, item) => sum + item.documents.length, 0) },
                  { label: 'Termine und Nachweise', value: data.coachings.reduce((sum, item) => sum + item.sessions.length, 0) },
                ]} />
              </FormSection>
              <FormSection label="Coachings">
                <CoachingCards items={data.coachings} onOpen={(item) => {
                  const landing = aufgabeForName(item.participation.aufgabe?.name)?.landing
                  dispatch({ type: 'OPEN_SCREEN', screen: { kind: landing?.modal ?? 'personal', tab: landing?.tab ?? 'details', coachingId: item.id, participantId: data.person.id }, remember: true })
                }} />
              </FormSection>
              {VariantSections && coachingId && <VariantSections coachingId={coachingId} />}
            </>}
            {state.screen?.tab === 'termine' && <>
              {!coachings.some((item) => item.sessions.length) && <p>Es sind keine Termine oder Nachweise vorhanden.</p>}
              {coachings.map((item) => item.sessions.map((session) => <FormSection key={String(session.id)} label={session.datum ? display(String(session.datum)) : 'Termin'}><Facts values={session} /></FormSection>))}
            </>}
          </>}
    </main>
    <ScrollBar scrollerRef={scrollerRef} />
  </>
}
